import { InfoIcon } from '@sharedCR/coreDesignSystem/icons';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { SitePlacement } from 'constants/loanApplication';
import {
  chattelHomeConditionOptions,
  manufacturedHomeTypeOptions,
  sitePlacementOptions,
} from 'constants/options';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys, getOptionLabelByValue } from 'utils/loanManagement';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import {
  customIconStyles,
  customIconWrapperStyles,
  customTitleStyles,
  customTitleWrapperStyles,
} from '../MainContent.styles';

const chattelPropertyConfigFields = {
  condition: {
    fieldId: 'condition',
    type: FieldType.SELECT,
    nameInModel: 'condition',
    inputProps: {
      options: chattelHomeConditionOptions,
      placeholder: 'Property Conditions',
      label: 'Property Conditions',
      disabled: false,
      required: true,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_FORM_CONDITION_SELECT,
    },
  },
  homeType: {
    fieldId: 'homeType',
    type: FieldType.SELECT,
    nameInModel: 'homeType',
    inputProps: {
      options: manufacturedHomeTypeOptions,
      // Resolve the label from the value against THIS field's options: the Manufactured Home Details
      // tab's Sections field shares the `homeType` form key with a different (short-form) label set,
      // so without this the Property tab could render the other tab's label for the same value.
      getOptionLabel: getOptionLabelByValue(manufacturedHomeTypeOptions),
      placeholder: 'Manufactured Home Type',
      label: 'Manufactured Home Type',
      disabled: false,
      required: true,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_FORM_HOME_TYPE_SELECT,
    },
  },
  sitePlacement: {
    fieldId: 'sitePlacement',
    type: FieldType.SELECT,
    nameInModel: 'sitePlacement',
    inputProps: {
      options: sitePlacementOptions,
      placeholder: 'Site Placement',
      label: 'Site Placement',
      disabled: false,
      required: true,
      // LEN-207: a one-column select clipped the longer placements ("Leased Private Property" read "Leased Pri...").
      columns: 2,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_FORM_SITE_PLACEMENT_SELECT,
    },
  },
  landownerName: {
    fieldId: 'landownerName',
    type: FieldType.INPUT,
    nameInModel: 'landownerName',
    inputProps: {
      placeholder: 'Landowner Name',
      label: 'Landowner Name',
      disabled: false,
      required: true,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_FORM_LANDOWNER_NAME_INPUT,
    },
  },
  landownerPhone: {
    fieldId: 'landownerPhone',
    type: FieldType.INPUT,
    nameInModel: 'landownerPhone',
    inputProps: {
      type: FieldInputType.TEL,
      placeholder: 'Landowner Phone',
      label: 'Landowner Phone',
      disabled: false,
      required: false,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_FORM_LANDOWNER_PHONE_INPUT,
    },
  },
  locationDescription: {
    fieldId: 'locationDescription',
    // The restyled FormGenerator has no TEXTAREA case -- a multiline INPUT is its text-area form.
    type: FieldType.INPUT,
    nameInModel: 'locationDescription',
    inputProps: {
      type: FieldInputType.TEXT,
      placeholder: 'Proposed location / legal description when the home has no 911 address yet',
      label: 'Location / Legal Description',
      disabled: false,
      required: false,
      maxLength: 1000,
      multiline: true,
      rows: 3,
      columns: 4,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_FORM_LOCATION_DESCRIPTION_INPUT,
    },
  },
  parkName: {
    fieldId: 'parkName',
    type: FieldType.INPUT,
    nameInModel: 'parkName',
    inputProps: {
      placeholder: 'Park Name',
      label: 'Park Name',
      disabled: false,
      required: true,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_FORM_PARK_NAME_INPUT,
    },
  },
  lotNumber: {
    fieldId: 'lotNumber',
    type: FieldType.INPUT,
    nameInModel: 'lotNumber',
    inputProps: {
      placeholder: 'Lot #',
      label: 'Lot #',
      disabled: false,
      required: true,
      dataTestId: PropertyAndLoanTestIds.CHATTEL_PROPERTY_FORM_LOT_NUMBER_INPUT,
    },
  },
};

export const getChattelPropertyConfig = compose(
  fillConfigFieldsWithKeys,
  ({ isLeasedPrivateProperty, isCommunityPark, isLandownerPlacement }) => ({
    groups: [
      {
        type: FieldType.GROUP,
        fields: [
          chattelPropertyConfigFields.condition,
          chattelPropertyConfigFields.homeType,
          chattelPropertyConfigFields.sitePlacement,
          ...getConditionalArray(isLandownerPlacement, [
            {
              ...chattelPropertyConfigFields.landownerName,
              inputProps: {
                ...chattelPropertyConfigFields.landownerName.inputProps,
                // Mandatory only where it always was (leased land); informational for family land / land contract.
                required: isLeasedPrivateProperty,
              },
            },
            chattelPropertyConfigFields.landownerPhone,
          ]),
          ...getConditionalArray(isCommunityPark, [
            chattelPropertyConfigFields.parkName,
            chattelPropertyConfigFields.lotNumber,
          ]),
          chattelPropertyConfigFields.locationDescription,
        ],
        key: 'chattel-property',
        title: {
          label: 'Property',
          icon: InfoIcon,
          titleStyles: customTitleStyles,
          wrapperStyles: customTitleWrapperStyles,
          iconWrapperStyles: customIconWrapperStyles,
          iconStyles: customIconStyles,
        },
      },
    ],
  }),
);

export const checkIsChattelLeasedPrivateProperty = sitePlacement =>
  sitePlacement === SitePlacement.LEASED_PRIVATE_PROPERTY;

export const checkIsChattelCommunityPark = sitePlacement =>
  sitePlacement === SitePlacement.COMMUNITY_PARK;

// LEN-207: SitePlacement is the source of truth; the obsolete isInPark bit only speaks for legacy / POS rows
// saved before a placement existed (it can only ever mean Community / Park).
export const getEffectiveChattelSitePlacement = chattelDetails =>
  chattelDetails?.sitePlacement ?? (chattelDetails?.isInPark ? SitePlacement.COMMUNITY_PARK : null);

export const getChattelSitePlacementLabel = chattelDetails =>
  getOptionLabelByValue(sitePlacementOptions)(getEffectiveChattelSitePlacement(chattelDetails)) ||
  null;

// LEN-133: the land under the home belongs to someone the lender may need to reach (leased, family land or
// land contract), so the landowner's name and phone are kept for these placements.
const CHATTEL_LANDOWNER_PLACEMENTS = [
  SitePlacement.LEASED_PRIVATE_PROPERTY,
  SitePlacement.FAMILY_LAND_NO_RENT,
  SitePlacement.OWNED_LAND_CONTRACT,
];

export const checkIsChattelLandownerPlacement = sitePlacement =>
  CHATTEL_LANDOWNER_PLACEMENTS.includes(sitePlacement);

export const checkIsChattelLandContract = sitePlacement =>
  sitePlacement === SitePlacement.OWNED_LAND_CONTRACT;
